import { useState, useCallback, useMemo, useEffect } from 'react'
import type { BooleanQuizData, QuizSolveResult } from '../../../types/legacyQuiz'

export interface UseBooleanQuizOptions {
  quizId: string | number
  quiz: BooleanQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  onAnswerChange?: (answers: string[]) => void
}

export function useBooleanQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange,
}: UseBooleanQuizOptions) {
  const [startTime] = useState(() => Date.now())
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false)

  const isSolved = solveResult !== null && solveResult !== undefined
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers
  const correctAnswer = solveResult?.correctAnswers?.[0] === 'true'

  const computedInitialSelected = useMemo(() => {
    if (initialAnswersProp !== undefined) {
      return initialAnswersProp.length > 0 ? initialAnswersProp[0] === 'true' : null
    }
    if (isViewSolved && solveResult?.correctAnswers) {
      return solveResult.correctAnswers[0] === 'true'
    }
    return null
  }, [isViewSolved, solveResult?.correctAnswers, initialAnswersProp])

  const [selected, setSelected] = useState<boolean | null>(computedInitialSelected)
  const canSubmit = selected !== null

  useEffect(() => {
    if (selected !== null) {
      onAnswerChange?.([selected.toString()])
    }
  }, [selected, onAnswerChange])

  const handleSelect = useCallback((value: boolean) => {
    if (isSolved || isViewSolved) return
    setSelected(value)
  }, [isSolved, isViewSolved])

  const handleSubmit = useCallback(() => {
    if (selected === null || isSolved) return
    const elapsedTime = Date.now() - startTime
    onSolve?.(quizId, [selected.toString()], quiz.vars, elapsedTime)
  }, [selected, isSolved, startTime, onSolve, quizId, quiz.vars])

  return {
    selected,
    isSolved,
    canSubmit,
    correctAnswer,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSelect,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved,
  }
}
